/**
 * useAlerts — reacts to new UT Bot signals and fires:
 *   1. Web Audio API tones (no external dependency)
 *   2. Browser Notification API (if permission granted)
 *
 * Subscribes to the Zustand store via subscribeWithSelector so it never
 * triggers a re-render — it's purely a side-effect listener.
 */

import { useEffect, useRef } from 'react';
import { useTerminalStore, selectAlertSettings } from '../stores/terminal.store.js';

// ─── Audio engine ─────────────────────────────────────────────────────────────

let audioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new AudioContext();
  }
  return audioCtx;
}

function playTone(
  frequency: number,
  duration: number,
  type: OscillatorType = 'sine',
  gainValue = 0.18
): void {
  try {
    const ctx  = getAudioCtx();
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type      = type;
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);

    gain.gain.setValueAtTime(gainValue, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  } catch {
    // AudioContext may be blocked until user interaction — silently ignore
  }
}

/** Iron Man-style ascending BUY chime: two rising tones */
function playBuySound(): void {
  playTone(520, 0.12, 'sine', 0.16);
  setTimeout(() => playTone(780, 0.18, 'sine', 0.14), 110);
}

/** Descending SELL alert: two falling tones */
function playSellSound(): void {
  playTone(680, 0.12, 'sine', 0.16);
  setTimeout(() => playTone(440, 0.18, 'sine', 0.14), 110);
}

// ─── Notification helper ──────────────────────────────────────────────────────

function fireNotification(title: string, body: string): void {
  if (Notification.permission !== 'granted') return;
  try {
    new Notification(title, {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'arc-signal',  // replaces previous notification (no spam)
    });
  } catch {
    // Notification API not available in this context
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAlerts(): void {
  const alertSettings  = useTerminalStore(selectAlertSettings);
  const signalCountRef = useRef<number>(0);

  // Request notification permission once on mount
  useEffect(() => {
    if (alertSettings.notificationsEnabled && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, [alertSettings.notificationsEnabled]);

  // Subscribe to signals map changes (not the derived array — avoids re-renders)
  useEffect(() => {
    const unsub = useTerminalStore.subscribe(
      state => state.signals,
      (signals) => {
        // Flatten all signals, sorted newest first
        const all = Object.values(signals)
          .flat()
          .sort((a, b) => b.timestamp - a.timestamp);

        const newCount = all.length;
        if (newCount <= signalCountRef.current) {
          signalCountRef.current = newCount;
          return;
        }

        // Process newly arrived signals
        const newSignals = all.slice(0, newCount - signalCountRef.current);
        signalCountRef.current = newCount;

        for (const signal of newSignals) {
          const isBuy  = signal.type === 'BUY';
          const isSell = signal.type === 'SELL';

          // Sound
          if (alertSettings.soundEnabled) {
            if (isBuy  && alertSettings.alertOnBuy)  playBuySound();
            if (isSell && alertSettings.alertOnSell) playSellSound();
          }

          // Browser notification
          if (alertSettings.notificationsEnabled) {
            if ((isBuy && alertSettings.alertOnBuy) || (isSell && alertSettings.alertOnSell)) {
              const emoji = isBuy ? '🟢' : '🔴';
              fireNotification(
                `${emoji} ${signal.type} — ${signal.symbol}`,
                `$${signal.price.toFixed(4)} · ATR ${signal.atr.toFixed(4)} · ${signal.timeframe}`
              );
            }
          }
        }
      }
    );

    return unsub;
  }, [alertSettings]);
}

// ─── Exported sound testers (used by AlertSettings modal) ─────────────────────

export function testBuySound():  void { playBuySound(); }
export function testSellSound(): void { playSellSound(); }

/** Three-tone ascending chime for price alert fires */
export function playAlertTone(): void {
  playTone(660,  0.10, 'square', 0.12);
  setTimeout(() => playTone(880,  0.15, 'sine', 0.14), 80);
  setTimeout(() => playTone(1100, 0.20, 'sine', 0.10), 180);
}
